import { Injectable } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot, Router, UrlTree } from '@angular/router';
import { Observable } from 'rxjs';
import { AuthService } from './auth.service';

export interface Permission {
    path: string;
    canAccess: boolean;
}

@Injectable({
    providedIn: 'root'
})
export class AuthGuard implements CanActivate {

    constructor(private router: Router, private authService: AuthService) {}

    canActivate(
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ): boolean | Observable<boolean> | Promise<boolean> {
       
        const userData = this.authService.getUserAuthSession();
        if (!userData) {
            console.warn('No user-auth data found. Redirecting to login.');
            this.router.navigate(['login']);
            return false;
        }

        try {
            let permissions: Permission[] = [];

            if (typeof userData.permissions === 'string') {
                permissions = JSON.parse(userData.permissions);
            } else {
                permissions = userData.permissions || [];
            }

            const fullPath = state.url.replace(/^\//, '');
            // If permissions are not present/empty, allow navigation (common in dev/local sessions).
            if (!Array.isArray(permissions) || permissions.length === 0) {
                return true;
            }

            // Support both exact route permissions and module-level permissions.
            // Examples:
            // - permission.path === "plans-per-sheet"
            // - permission.path prefix for nested routes under a module
            const hasAccess = permissions.some(p => {
                if (!p?.path || !p.canAccess) return false;
                if (p.path === fullPath) return true;
                // Allow access when permission is for parent path/module.
                return fullPath === p.path || fullPath.startsWith(`${p.path}/`);
            });

            if (!hasAccess) {
                console.warn(`Access denied to ${fullPath}. No matching permission found. Permissions:`, permissions);
                this.router.navigate(['login']);
                return false;
            }

            return true;
        } catch (error) {
            console.error('Error parsing user-auth data:', error);
            this.router.navigate(['login']);
            return false;
        }
    }
}

@Injectable({
    providedIn: 'root'
})
export class GuestGuard implements CanActivate {

    constructor(private router: Router, private authService: AuthService) {}

    canActivate(route: ActivatedRouteSnapshot): boolean | UrlTree {
        if (route.queryParams['token']) {
            return true;
        }

        if (this.authService.isUserExist()) {
            return this.router.createUrlTree(['/sales-dashboard']);
        }

        return true;
    }
}